import { content as defaultContent } from '../content';
import { seedFromString, shuffle, randomInt } from './rng';
import type {
  AtomicEffect,
  CardDefinition,
  CardEffect,
  CardInstance,
  CombatState,
  ContentRegistry,
  EnemyInstance,
  EnemyMove,
  EngineResult,
  EventChoiceDefinition,
  EventEffect,
  GameCommand,
  GameEvent,
  GameState,
  RelicModifiers,
  RouteNodeKind,
  RouteOption,
  RunState,
  StatusId,
  StatusMap,
} from './types';

const MAX_HAND_SIZE = 10;
const BASE_DRAW = 5;
const BASE_ENERGY = 3;
const MAX_HEAT = 6;
const OVERHEAT_DAMAGE = 3;
const SHOP_REMOVAL_PRICE = 45;
const SHOP_REPAIR_PRICE = 10;

export class GameRuleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GameRuleError';
  }
}

export function emptyStatuses(): StatusMap {
  return {
    strength: 0,
    dexterity: 0,
    weak: 0,
    vulnerable: 0,
    burning: 0,
    thorns: 0,
  };
}

export function createInitialState(): GameState {
  return {
    schemaVersion: 1,
    contentVersion: defaultContent.version,
    screen: 'title',
    seed: '',
    rng: seedFromString('ironvein'),
    nextId: 1,
  };
}

function nextId(state: GameState, prefix: string): string {
  const id = `${prefix}-${state.nextId}`;
  state.nextId += 1;
  return id;
}

function relicModifier(
  state: GameState,
  registry: ContentRegistry,
  modifier: keyof RelicModifiers,
): number {
  const run = state.run;
  if (!run) return 0;
  return run.relics.reduce((total, relicId) => {
    const value = registry.relics[relicId]?.modifiers[modifier];
    return total + (typeof value === 'number' ? value : 0);
  }, 0);
}

function repairAllGear(state: GameState, registry: ContentRegistry, events: GameEvent[]): void {
  const run = requireRun(state);
  for (const instance of run.deck) {
    const definition = resolveCardDefinition(instance, registry);
    if (!definition.gear || instance.durability === undefined) continue;
    if (instance.durability === definition.gear.maxDurability) continue;
    instance.durability = definition.gear.maxDurability;
    events.push({ type: 'gear_repaired', cardId: instance.id, durability: instance.durability });
  }
}

function acquireRelic(
  state: GameState,
  relicId: string,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const run = requireRun(state);
  const relic = registry.relics[relicId];
  if (!relic) throw new GameRuleError(`Unknown relic: ${relicId}`);
  if (run.relics.includes(relicId)) {
    run.iron += 15;
    events.push({ type: 'iron_changed', amount: 15 });
    return;
  }

  run.relics.push(relicId);
  const maxHpChange = relic.modifiers.maxHp ?? 0;
  if (maxHpChange !== 0) {
    run.maxHp = Math.max(1, run.maxHp + maxHpChange);
    run.hp = Math.min(run.maxHp, Math.max(1, run.hp + maxHpChange));
  }
  events.push({ type: 'relic_gained', relicId });
}

function pickOne<T>(state: GameState, values: readonly T[]): T {
  if (values.length === 0) throw new GameRuleError('Cannot choose from an empty content pool.');
  const roll = randomInt(state.rng, 0, values.length - 1);
  state.rng = roll.state;
  const value = values[roll.value];
  if (value === undefined) throw new GameRuleError('Random choice produced no value.');
  return value;
}

type RewardRarity = 'common' | 'uncommon' | 'rare';
type CardOfferContext = 'normal' | 'elite' | 'shop';

function rollRarity(
  state: GameState,
  weights: Record<RewardRarity, number>,
): RewardRarity {
  const roll = randomInt(state.rng, 1, 100);
  state.rng = roll.state;
  if (roll.value <= weights.common) return 'common';
  if (roll.value <= weights.common + weights.uncommon) return 'uncommon';
  return 'rare';
}

function rarityWeights(
  act: number,
  context: CardOfferContext,
  actStep = 1,
): Record<RewardRarity, number> {
  if (context === 'shop') {
    return act === 1
      ? { common: 70, uncommon: 25, rare: 5 }
      : act === 2
        ? { common: 40, uncommon: 45, rare: 15 }
        : { common: 20, uncommon: 50, rare: 30 };
  }
  if (context === 'elite') {
    return act === 1
      ? { common: 45, uncommon: 45, rare: 10 }
      : act === 2
        ? { common: 30, uncommon: 50, rare: 20 }
        : { common: 20, uncommon: 50, rare: 30 };
  }
  const normalProfiles: Record<number, Record<number, Record<RewardRarity, number>>> = {
    1: {
      0: { common: 80, uncommon: 19, rare: 1 },
      1: { common: 75, uncommon: 22, rare: 3 },
      3: { common: 65, uncommon: 30, rare: 5 },
    },
    2: {
      0: { common: 65, uncommon: 30, rare: 5 },
      1: { common: 60, uncommon: 33, rare: 7 },
      3: { common: 55, uncommon: 35, rare: 10 },
    },
    3: {
      0: { common: 55, uncommon: 35, rare: 10 },
      1: { common: 50, uncommon: 38, rare: 12 },
      3: { common: 45, uncommon: 40, rare: 15 },
    },
  };
  return normalProfiles[act]?.[actStep] ?? normalProfiles[act]?.[1] ?? normalProfiles[3]![1]!;
}

function pickRewardCard(
  state: GameState,
  registry: ContentRegistry,
  rarity: RewardRarity,
  excluded: Set<string>,
): string {
  let pool = registry.rewardPool.filter(
    (cardId) => registry.cards[cardId]?.rarity === rarity && !excluded.has(cardId),
  );
  if (pool.length === 0) pool = registry.rewardPool.filter((cardId) => !excluded.has(cardId));
  const chosen = pickOne(state, pool);
  excluded.add(chosen);
  return chosen;
}

function cardChoices(
  state: GameState,
  registry: ContentRegistry,
  act: number,
  actStep: number,
  context: 'normal' | 'elite',
): string[] {
  const excluded = new Set<string>();
  const weights = rarityWeights(act, context, actStep);
  const secondRarity = rollRarity(state, weights);
  let thirdRarity = rollRarity(state, weights);
  if (context === 'normal' && secondRarity === 'rare' && thirdRarity === 'rare') {
    thirdRarity = 'uncommon';
  }
  return [
    pickRewardCard(state, registry, 'common', excluded),
    pickRewardCard(state, registry, secondRarity, excluded),
    pickRewardCard(state, registry, thirdRarity, excluded),
  ];
}

function makeRouteOption(
  state: GameState,
  kind: RouteNodeKind,
  label: string,
  description: string,
  details: Pick<RouteOption, 'encounterId' | 'eventId'> = {},
): RouteOption {
  return {
    id: nextId(state, 'route'),
    kind,
    label,
    description,
    ...details,
  };
}

function encounterForRoute(
  state: GameState,
  registry: ContentRegistry,
  act: number,
  kind: 'normal' | 'elite' | 'boss',
) {
  const seen = state.run?.encountersSeen ?? [];
  let pool = Object.values(registry.encounters).filter(
    (encounter) => encounter.act === act && encounter.kind === kind && !seen.includes(encounter.id),
  );
  if (pool.length === 0) {
    pool = Object.values(registry.encounters).filter(
      (encounter) => encounter.act === act && encounter.kind === kind,
    );
  }
  return pickOne(state, pool);
}

function generateRouteOptions(state: GameState, registry: ContentRegistry): void {
  const run = requireRun(state);
  const normal = () => encounterForRoute(state, registry, run.act, 'normal');

  switch (run.actStep) {
    case 0: {
      const introIds: Record<number, string> = {
        1: 'gallery_intro',
        2: 'ember_first_sparks',
        3: 'foundry_first_cast',
      };
      const encounter = registry.encounters[introIds[run.act] ?? ''] ?? normal();
      run.routeOptions = [
        makeRouteOption(state, 'combat', encounter.name, 'A required clash on the supply line.', {
          encounterId: encounter.id,
        }),
      ];
      return;
    }
    case 1: {
      const encounter = normal();
      const event = pickOne(
        state,
        Object.values(registry.events).filter((candidate) => candidate.act === run.act),
      );
      run.routeOptions = [
        makeRouteOption(state, 'combat', encounter.name, 'Risk blood for Iron and a new technique.', {
          encounterId: encounter.id,
        }),
        makeRouteOption(state, 'event', event.title, 'Follow an uncertain sound off the main rail.', {
          eventId: event.id,
        }),
      ];
      return;
    }
    case 2: {
      const encounter = encounterForRoute(state, registry, run.act, 'elite');
      run.routeOptions = [
        makeRouteOption(state, 'elite', encounter.name, 'A lethal fight guarding a rare relic.', {
          encounterId: encounter.id,
        }),
        makeRouteOption(state, 'trader', 'Field Quartermaster', 'Spend Iron on cards, relics, and repairs.'),
      ];
      return;
    }
    case 3: {
      const encounter = normal();
      run.routeOptions = [
        makeRouteOption(state, 'forge', 'Field Workshop', 'Heal, upgrade one card, or repair all Gear.'),
        makeRouteOption(state, 'combat', encounter.name, 'Take one more fight before the act guardian.', {
          encounterId: encounter.id,
        }),
      ];
      return;
    }
    case 4: {
      const encounter = encounterForRoute(state, registry, run.act, 'boss');
      run.routeOptions = [
        makeRouteOption(state, 'boss', encounter.name, 'The line stays closed until this guardian is defeated.', {
          encounterId: encounter.id,
        }),
      ];
      return;
    }
    default:
      run.routeOptions = [];
  }
}

function completeNode(state: GameState, registry: ContentRegistry): void {
  const run = requireRun(state);
  run.node += 1;
  run.actStep += 1;
  delete state.event;
  delete state.shop;

  if (run.actStep > 4) {
    run.act += 1;
    run.actStep = 0;
  }

  if (run.act > 3) {
    run.routeOptions = [];
    state.screen = 'victory';
    return;
  }

  state.screen = 'route';
  generateRouteOptions(state, registry);
}

function discountedPrice(
  state: GameState,
  registry: ContentRegistry,
  basePrice: number,
): number {
  const discount = Math.min(90, relicModifier(state, registry, 'shopDiscountPercent'));
  return Math.max(1, Math.ceil(basePrice * (1 - discount / 100)));
}

function generateShop(state: GameState, registry: ContentRegistry): void {
  const run = requireRun(state);
  const excluded = new Set<string>();
  const shopCards = [
    pickRewardCard(state, registry, 'common', excluded),
    pickRewardCard(state, registry, 'uncommon', excluded),
    pickRewardCard(
      state,
      registry,
      rollRarity(state, rarityWeights(run.act, 'shop')),
      excluded,
    ),
  ];
  const relicPool = Object.values(registry.relics).filter(
    (relic) => relic.rarity === 'common' && !run.relics.includes(relic.id),
  );
  const relic = relicPool.length > 0 ? pickOne(state, relicPool) : undefined;
  state.shop = {
    cards: shopCards.map((cardDefinitionId) => {
      const rarity = registry.cards[cardDefinitionId]?.rarity;
      const basePrice = rarity === 'rare'
        ? 70 + (run.act - 1) * 8
        : rarity === 'uncommon'
          ? 42 + (run.act - 1) * 6
          : 24 + (run.act - 1) * 4;
      return {
        id: nextId(state, 'offer'),
        cardDefinitionId,
        price: discountedPrice(state, registry, basePrice),
        sold: false,
      };
    }),
    ...(relic
      ? {
          relic: {
            id: nextId(state, 'offer'),
            relicId: relic.id,
            price: discountedPrice(state, registry, 70 + (run.act - 1) * 12),
            sold: false,
          },
        }
      : {}),
    removalPrice: discountedPrice(state, registry, SHOP_REMOVAL_PRICE + (run.act - 1) * 10),
    repairPrice: discountedPrice(state, registry, SHOP_REPAIR_PRICE + 2 + (run.act - 1) * 3),
    removalUsed: false,
  };
}

function requireRun(state: GameState): RunState {
  if (!state.run) throw new GameRuleError('No active run.');
  return state.run;
}

function requireCombat(state: GameState): CombatState {
  if (!state.combat || state.screen !== 'combat') throw new GameRuleError('No active combat.');
  return state.combat;
}

function makeCardInstance(
  state: GameState,
  definitionId: string,
  registry: ContentRegistry,
  temporary = false,
): CardInstance {
  const definition = registry.cards[definitionId];
  if (!definition) throw new GameRuleError(`Unknown card: ${definitionId}`);

  return {
    id: nextId(state, 'card'),
    definitionId,
    upgraded: false,
    ...(temporary ? { temporary: true } : {}),
    ...(definition.gear ? { durability: definition.gear.maxDurability } : {}),
  };
}

export function resolveCardDefinition(
  instance: CardInstance,
  registry: ContentRegistry = defaultContent,
): CardDefinition {
  const base = registry.cards[instance.definitionId];
  if (!base) throw new GameRuleError(`Unknown card definition: ${instance.definitionId}`);
  if (!instance.upgraded || !base.upgrade) return base;

  return {
    ...base,
    ...base.upgrade,
    effects: base.upgrade.effects ?? base.effects,
  };
}

export function getCardInstance(state: GameState, cardId: string): CardInstance {
  const card = requireRun(state).deck.find((candidate) => candidate.id === cardId);
  if (!card) throw new GameRuleError(`Unknown card instance: ${cardId}`);
  return card;
}

export function getEnemyIntent(
  _state: GameState,
  enemy: EnemyInstance,
  registry: ContentRegistry = defaultContent,
): EnemyMove {
  const definition = registry.enemies[enemy.definitionId];
  if (!definition) throw new GameRuleError(`Unknown enemy definition: ${enemy.definitionId}`);
  const phase = enemy.phaseIndex > 0 ? definition.phases?.[enemy.phaseIndex - 1] : undefined;
  const pattern = phase?.pattern ?? definition.pattern;
  const move = pattern[enemy.moveIndex % pattern.length];
  if (!move) throw new GameRuleError(`Enemy ${definition.id} has an empty pattern.`);
  return move;
}

export function getEnemyIntentDamage(
  state: GameState,
  enemy: EnemyInstance,
  move: EnemyMove = getEnemyIntent(state, enemy),
): number | undefined {
  if (move.kind !== 'attack' && move.kind !== 'attack_status') return undefined;
  return enemyDamage(enemy, move.amount, requireCombat(state));
}

export function getOverheatDamage(state: GameState, registry: ContentRegistry = defaultContent): number {
  return Math.max(0, OVERHEAT_DAMAGE + relicModifier(state, registry, 'overheatDamageBonus'));
}

function drawCards(state: GameState, amount: number, events: GameEvent[]): void {
  const combat = requireCombat(state);

  for (let draw = 0; draw < amount && combat.hand.length < MAX_HAND_SIZE; draw += 1) {
    if (combat.drawPile.length === 0) {
      if (combat.discardPile.length === 0) return;
      const reshuffled = shuffle(state.rng, combat.discardPile);
      state.rng = reshuffled.state;
      combat.drawPile = reshuffled.values;
      combat.discardPile = [];
    }

    const cardId = combat.drawPile.pop();
    if (!cardId) return;
    combat.hand.push(cardId);
    events.push({ type: 'card_drawn', cardId });
  }
}

function aliveEnemies(combat: CombatState): EnemyInstance[] {
  return combat.enemies.filter((enemy) => enemy.hp > 0);
}

function scaledPlayerDamage(
  state: GameState,
  registry: ContentRegistry,
  amount: number,
  combat: CombatState,
  enemy: EnemyInstance,
  sourceIsAttack: boolean,
): number {
  let damage = Math.max(
    0,
    amount + combat.statuses.strength +
      (sourceIsAttack ? relicModifier(state, registry, 'attackBonus') : 0),
  );
  if (combat.statuses.weak > 0) damage = Math.floor(damage * 0.75);
  if (enemy.statuses.vulnerable > 0) damage = Math.floor(damage * 1.5);
  return Math.max(0, damage);
}

function damageEnemy(
  enemy: EnemyInstance,
  amount: number,
  sourceId: string,
  events: GameEvent[],
): void {
  if (enemy.hp <= 0) return;
  const blocked = Math.min(enemy.block, amount);
  enemy.block -= blocked;
  const dealt = Math.max(0, amount - blocked);
  enemy.hp = Math.max(0, enemy.hp - dealt);
  events.push({ type: 'damage', sourceId, targetId: enemy.id, amount: dealt, blocked });
  if (enemy.hp === 0) events.push({ type: 'enemy_defeated', enemyId: enemy.id });
}

function changeHeat(combat: CombatState, amount: number, events: GameEvent[]): void {
  const from = combat.heat;
  combat.heat = Math.max(0, Math.min(MAX_HEAT, combat.heat + amount));
  if (from !== combat.heat) events.push({ type: 'heat_changed', from, to: combat.heat });
}

function applyStatus(
  targetId: string,
  statuses: StatusMap,
  status: StatusId,
  amount: number,
  events: GameEvent[],
): void {
  statuses[status] = Math.max(0, statuses[status] + amount);
  events.push({ type: 'status_changed', targetId, status, amount });
}

function updateEnemyPhases(
  state: GameState,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const combat = requireCombat(state);
  for (const enemy of aliveEnemies(combat)) {
    const definition = registry.enemies[enemy.definitionId];
    if (!definition) throw new GameRuleError(`Unknown enemy definition: ${enemy.definitionId}`);
    let nextPhase = definition.phases?.[enemy.phaseIndex];
    while (nextPhase && enemy.hp <= nextPhase.atHp) {
      enemy.phaseIndex += 1;
      enemy.moveIndex = 0;
      if (nextPhase.onEnterBlock) {
        enemy.block += nextPhase.onEnterBlock;
        events.push({ type: 'block_gained', targetId: enemy.id, amount: nextPhase.onEnterBlock });
      }
      if (nextPhase.onEnterStrength) {
        applyStatus(enemy.id, enemy.statuses, 'strength', nextPhase.onEnterStrength, events);
      }
      events.push({ type: 'enemy_phase_changed', enemyId: enemy.id, phase: enemy.phaseIndex + 1 });
      nextPhase = definition.phases?.[enemy.phaseIndex];
    }
  }
}

function repairGear(
  state: GameState,
  amount: number,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const run = requireRun(state);
  const damaged = run.deck
    .map((instance) => ({ instance, definition: resolveCardDefinition(instance, registry) }))
    .filter(
      ({ instance, definition }) =>
        definition.gear &&
        instance.durability !== undefined &&
        instance.durability < definition.gear.maxDurability,
    )
    .sort((left, right) => (left.instance.durability ?? 0) - (right.instance.durability ?? 0));

  const target = damaged[0];
  if (!target?.definition.gear || target.instance.durability === undefined) return;
  target.instance.durability = Math.min(
    target.definition.gear.maxDurability,
    target.instance.durability + amount,
  );
  events.push({
    type: 'gear_repaired',
    cardId: target.instance.id,
    durability: target.instance.durability,
  });
}

function wearGearFromEnemy(
  state: GameState,
  amount: number,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const target = requireRun(state).deck
    .filter((instance) => !instance.temporary && (instance.durability ?? 0) > 0)
    .map((instance) => ({ instance, definition: resolveCardDefinition(instance, registry) }))
    .filter(({ definition }) => Boolean(definition.gear))
    .sort((left, right) =>
      (left.instance.durability ?? 0) - (right.instance.durability ?? 0) ||
      left.instance.id.localeCompare(right.instance.id),
    )[0];
  if (!target || target.instance.durability === undefined) return;
  wearGear(state, target.instance, amount, registry, events);
}

function wearGear(
  state: GameState,
  instance: CardInstance,
  amount: number,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (instance.durability === undefined || amount <= 0) return;
  const previousDurability = instance.durability;
  instance.durability = Math.max(0, previousDurability - amount);
  events.push({
    type: 'gear_worn',
    cardId: instance.id,
    durability: instance.durability,
  });

  if (previousDurability > 0 && instance.durability === 0) {
    const breakBlock = relicModifier(state, registry, 'blockOnGearBreak');
    if (breakBlock > 0) {
      const combat = requireCombat(state);
      combat.block += breakBlock;
      events.push({ type: 'block_gained', targetId: 'player', amount: breakBlock });
    }
  }
}

function insertStatusCards(
  state: GameState,
  definitionId: string,
  amount: number,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const definition = registry.cards[definitionId];
  if (!definition || definition.rarity !== 'status') {
    throw new GameRuleError(`Unknown status card: ${definitionId}`);
  }
  const run = requireRun(state);
  const combat = requireCombat(state);
  for (let index = 0; index < amount; index += 1) {
    const card = makeCardInstance(state, definitionId, registry, true);
    run.deck.push(card);
    combat.discardPile.push(card.id);
    events.push({ type: 'status_inserted', cardId: card.id, definitionId });
  }
}

function purgeTemporaryCards(state: GameState): void {
  const run = requireRun(state);
  const temporaryIds = new Set(
    run.deck.filter((card) => card.temporary).map((card) => card.id),
  );
  if (temporaryIds.size === 0) return;
  run.deck = run.deck.filter((card) => !temporaryIds.has(card.id));
  const combat = state.combat;
  if (!combat) return;
  combat.hand = combat.hand.filter((cardId) => !temporaryIds.has(cardId));
  combat.drawPile = combat.drawPile.filter((cardId) => !temporaryIds.has(cardId));
  combat.discardPile = combat.discardPile.filter((cardId) => !temporaryIds.has(cardId));
  combat.exhaustPile = combat.exhaustPile.filter((cardId) => !temporaryIds.has(cardId));
}

function salvageableBrokenGear(
  state: GameState,
  registry: ContentRegistry,
): CardInstance | undefined {
  const run = requireRun(state);
  const combat = requireCombat(state);
  const activeCardIds = new Set([
    ...combat.hand,
    ...combat.drawPile,
    ...combat.discardPile,
  ]);

  return run.deck.find((instance) =>
    activeCardIds.has(instance.id) &&
    instance.durability === 0 &&
    Boolean(resolveCardDefinition(instance, registry).gear),
  );
}

function salvageBrokenGear(state: GameState, registry: ContentRegistry): boolean {
  const target = salvageableBrokenGear(state, registry);
  if (!target) return false;
  const combat = requireCombat(state);
  combat.hand = combat.hand.filter((cardId) => cardId !== target.id);
  combat.drawPile = combat.drawPile.filter((cardId) => cardId !== target.id);
  combat.discardPile = combat.discardPile.filter((cardId) => cardId !== target.id);
  combat.exhaustPile.push(target.id);
  return true;
}

function hasResolvableEffect(
  state: GameState,
  definition: CardDefinition,
  registry: ContentRegistry,
): boolean {
  const combat = requireCombat(state);
  return definition.effects.some((effect) => {
    if (effect.kind === 'if_heat') return combat.heat >= effect.minimum;
    if (effect.kind === 'salvage') return Boolean(salvageableBrokenGear(state, registry));
    return true;
  });
}

function applyAtomicEffect(
  state: GameState,
  effect: AtomicEffect,
  sourceId: string,
  selectedTargetId: string | undefined,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const combat = requireCombat(state);

  switch (effect.kind) {
    case 'damage': {
      const source = getCardInstance(state, sourceId);
      const sourceIsAttack = resolveCardDefinition(source, registry).type === 'attack';
      const targets = effect.target === 'all'
        ? aliveEnemies(combat)
        : aliveEnemies(combat).filter((enemy) => enemy.id === selectedTargetId);
      const hits = effect.hits ?? 1;
      for (const enemy of targets) {
        for (let hit = 0; hit < hits && enemy.hp > 0; hit += 1) {
          damageEnemy(
            enemy,
            scaledPlayerDamage(state, registry, effect.amount, combat, enemy, sourceIsAttack),
            sourceId,
            events,
          );
        }
      }
      return;
    }
    case 'block': {
      const amount = Math.max(
        0,
        effect.amount +
          combat.statuses.dexterity +
          relicModifier(state, registry, 'blockBonus'),
      );
      combat.block += amount;
      events.push({ type: 'block_gained', targetId: 'player', amount });
      return;
    }
    case 'draw':
      drawCards(state, effect.amount, events);
      return;
    case 'heat':
      changeHeat(combat, effect.amount, events);
      return;
    case 'status': {
      if (effect.target === 'self') {
        applyStatus('player', combat.statuses, effect.status, effect.amount, events);
        return;
      }
      const targets = effect.target === 'all'
        ? aliveEnemies(combat)
        : aliveEnemies(combat).filter((enemy) => enemy.id === selectedTargetId);
      for (const target of targets) {
        applyStatus(target.id, target.statuses, effect.status, effect.amount, events);
      }
      return;
    }
    case 'repair':
      repairGear(state, effect.amount, registry, events);
  }
}

function applyEffect(
  state: GameState,
  effect: CardEffect,
  sourceId: string,
  selectedTargetId: string | undefined,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (effect.kind === 'salvage') {
    if (!salvageBrokenGear(state, registry)) return;
    for (const nestedEffect of effect.effects) {
      applyAtomicEffect(state, nestedEffect, sourceId, selectedTargetId, registry, events);
    }
    return;
  }

  if (effect.kind !== 'if_heat') {
    applyAtomicEffect(state, effect, sourceId, selectedTargetId, registry, events);
    return;
  }

  const combat = requireCombat(state);
  if (combat.heat < effect.minimum) return;
  if (effect.consume) changeHeat(combat, -effect.consume, events);
  for (const nestedEffect of effect.effects) {
    applyAtomicEffect(state, nestedEffect, sourceId, selectedTargetId, registry, events);
  }
}

function rewardForVictory(
  state: GameState,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const combat = requireCombat(state);
  const run = requireRun(state);
  const encounter = registry.encounters[combat.encounterId];
  if (!encounter) throw new GameRuleError(`Unknown encounter: ${combat.encounterId}`);
  combat.phase = 'won';
  run.combatsWon += 1;
  purgeTemporaryCards(state);

  let baseIron = 0;
  if (encounter.kind === 'normal') {
    const ranges: Record<number, [number, number]> = { 1: [12, 16], 2: [17, 22], 3: [22, 28] };
    const [minimum, maximum] = ranges[run.act] ?? ranges[3]!;
    const ironRoll = randomInt(state.rng, minimum, maximum);
    state.rng = ironRoll.state;
    baseIron = ironRoll.value;
  } else if (encounter.kind === 'elite') {
    baseIron = run.act === 1 ? 28 : run.act === 2 ? 38 : 50;
  }
  const iron = Math.max(0, baseIron + relicModifier(state, registry, 'rewardIron'));

  const postCombatHeal = relicModifier(state, registry, 'healAfterCombat');
  if (postCombatHeal > 0) run.hp = Math.min(run.maxHp, run.hp + postCombatHeal);
  const postCombatRepair = relicModifier(state, registry, 'repairAfterCombat');
  if (postCombatRepair > 0) repairGear(state, postCombatRepair, registry, events);

  if (encounter.kind === 'boss') {
    run.bossesDefeated += 1;
    const recoveryRate = 0.3;
    run.hp = Math.min(run.maxHp, run.hp + Math.ceil(run.maxHp * recoveryRate));
    repairAllGear(state, registry, events);
    if (run.act === 3 && run.actStep === 4) {
      run.node += 1;
      run.iron += iron;
      if (iron !== 0) events.push({ type: 'iron_changed', amount: iron });
      run.routeOptions = [];
      state.screen = 'victory';
      events.push({ type: 'combat_won', iron });
      return;
    }
  }

  let relicIds: string[] = [];
  if (encounter.kind === 'boss') {
    const bossOrder = shuffle(
      state.rng,
      Object.values(registry.relics)
        .filter((relic) => relic.rarity === 'boss' && !run.relics.includes(relic.id))
        .map((relic) => relic.id),
    );
    state.rng = bossOrder.state;
    relicIds = bossOrder.values.slice(0, 3);
  } else if (encounter.kind === 'elite') {
    const eliteOrder = shuffle(
      state.rng,
      Object.values(registry.relics)
        .filter((relic) => relic.rarity === 'elite' && !run.relics.includes(relic.id))
        .map((relic) => relic.id),
    );
    state.rng = eliteOrder.state;
    const commonOrder = shuffle(
      state.rng,
      Object.values(registry.relics)
        .filter((relic) => relic.rarity === 'common' && !run.relics.includes(relic.id))
        .map((relic) => relic.id),
    );
    state.rng = commonOrder.state;
    relicIds = [...eliteOrder.values.slice(0, 1), ...commonOrder.values.slice(0, 2)];
  }
  state.reward = {
    kind: encounter.kind,
    cardIds: encounter.kind === 'boss'
      ? []
      : cardChoices(state, registry, run.act, run.actStep, encounter.kind),
    relicIds,
    iron,
  };
  state.screen = 'reward';
  events.push({ type: 'combat_won', iron });
}

function loseCombat(state: GameState, events: GameEvent[]): void {
  const combat = state.combat;
  if (combat) combat.phase = 'lost';
  if (state.run) purgeTemporaryCards(state);
  state.screen = 'defeat';
  events.push({ type: 'combat_lost' });
}

function enemyDamage(enemy: EnemyInstance, amount: number, combat: CombatState): number {
  let damage = Math.max(0, amount + enemy.statuses.strength);
  if (enemy.statuses.weak > 0) damage = Math.floor(damage * 0.75);
  if (combat.statuses.vulnerable > 0) damage = Math.floor(damage * 1.5);
  return Math.max(0, damage);
}

function damagePlayer(
  state: GameState,
  amount: number,
  sourceId: string,
  events: GameEvent[],
  bypassBlock = false,
): number {
  const run = requireRun(state);
  const combat = requireCombat(state);
  const blocked = bypassBlock ? 0 : Math.min(combat.block, amount);
  if (!bypassBlock) combat.block -= blocked;
  const dealt = Math.max(0, amount - blocked);
  run.hp = Math.max(0, run.hp - dealt);
  events.push({ type: 'damage', sourceId, targetId: 'player', amount: dealt, blocked });
  return dealt;
}

function executeEnemyMove(
  state: GameState,
  enemy: EnemyInstance,
  move: EnemyMove,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const combat = requireCombat(state);
  enemy.block = 0;

  switch (move.kind) {
    case 'attack': {
      const hits = move.hits ?? 1;
      let hpDamage = 0;
      for (let hit = 0; hit < hits && requireRun(state).hp > 0 && enemy.hp > 0; hit += 1) {
        hpDamage += damagePlayer(state, enemyDamage(enemy, move.amount, combat), enemy.id, events);
        if (combat.statuses.thorns > 0 && enemy.hp > 0) {
          damageEnemy(enemy, combat.statuses.thorns, 'player', events);
        }
      }
      if (move.playerHeat) changeHeat(combat, move.playerHeat, events);
      if (hpDamage > 0 && move.gearWearOnHpDamage) {
        wearGearFromEnemy(state, move.gearWearOnHpDamage, registry, events);
      }
      if (move.statusCard && requireRun(state).hp > 0) {
        insertStatusCards(state, move.statusCard.definitionId, move.statusCard.amount, registry, events);
      }
      break;
    }
    case 'attack_status': {
      const hpDamage = damagePlayer(state, enemyDamage(enemy, move.amount, combat), enemy.id, events);
      if (combat.statuses.thorns > 0 && enemy.hp > 0) {
        damageEnemy(enemy, combat.statuses.thorns, 'player', events);
      }
      if (requireRun(state).hp > 0) {
        applyStatus('player', combat.statuses, move.status, move.statusAmount, events);
      }
      if (move.playerHeat) changeHeat(combat, move.playerHeat, events);
      if (hpDamage > 0 && move.gearWearOnHpDamage) {
        wearGearFromEnemy(state, move.gearWearOnHpDamage, registry, events);
      }
      if (move.statusCard && requireRun(state).hp > 0) {
        insertStatusCards(state, move.statusCard.definitionId, move.statusCard.amount, registry, events);
      }
      break;
    }
    case 'block':
      enemy.block += move.amount;
      events.push({ type: 'block_gained', targetId: enemy.id, amount: move.amount });
      break;
    case 'buff':
      applyStatus(enemy.id, enemy.statuses, move.status, move.amount, events);
  }

  enemy.moveIndex += 1;
  if (enemy.statuses.weak > 0) enemy.statuses.weak -= 1;
}

function dispatchStartRun(
  state: GameState,
  command: Extract<GameCommand, { type: 'start_run' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const seed = command.seed.trim();
  if (!seed) throw new GameRuleError('A run seed is required.');

  state.seed = seed;
  state.rng = seedFromString(seed);
  state.contentVersion = registry.version;
  state.nextId = 1;
  state.run = {
    hp: 72,
    maxHp: 72,
    iron: 12,
    difficulty: command.difficulty ?? 'standard',
    act: 1,
    actStep: 0,
    node: 0,
    deck: registry.starterDeck.map((cardId) => makeCardInstance(state, cardId, registry)),
    relics: ['smiths_mark'],
    combatsWon: 0,
    bossesDefeated: 0,
    encountersSeen: [],
    routeOptions: [],
  };
  delete state.combat;
  delete state.reward;
  delete state.event;
  delete state.shop;
  state.screen = 'route';
  generateRouteOptions(state, registry);
  events.push({ type: 'run_started', seed });
}

function dispatchStartCombat(
  state: GameState,
  command: Extract<GameCommand, { type: 'start_combat' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (state.screen !== 'route') throw new GameRuleError('Combat can only begin from the route.');
  const run = requireRun(state);
  const encounter = registry.encounters[command.encounterId];
  if (!encounter) throw new GameRuleError(`Unknown encounter: ${command.encounterId}`);
  const offeredRoute = run.routeOptions.find((option) => option.encounterId === encounter.id);
  const expectedRouteKind = encounter.kind === 'normal' ? 'combat' : encounter.kind;
  if (!offeredRoute || offeredRoute.kind !== expectedRouteKind) {
    throw new GameRuleError('That encounter is not available on the current route.');
  }
  if (encounter.act !== run.act) {
    throw new GameRuleError('That encounter belongs to a different act.');
  }
  if (!run.encountersSeen.includes(encounter.id)) run.encountersSeen.push(encounter.id);

  const shuffled = shuffle(state.rng, run.deck.map((card) => card.id));
  state.rng = shuffled.state;
  const maxEnergy = Math.max(1, BASE_ENERGY + relicModifier(state, registry, 'maxEnergy'));
  const statuses = emptyStatuses();
  statuses.strength = Math.max(0, relicModifier(state, registry, 'startStrength'));
  statuses.thorns = Math.max(0, relicModifier(state, registry, 'startThorns'));
  statuses.vulnerable = Math.max(0, relicModifier(state, registry, 'startVulnerable'));
  state.combat = {
    encounterId: encounter.id,
    phase: 'player',
    turn: 1,
    energy: maxEnergy,
    maxEnergy,
    heat: Math.max(0, Math.min(MAX_HEAT, relicModifier(state, registry, 'startHeat'))),
    block: Math.max(0, relicModifier(state, registry, 'startBlock')),
    statuses,
    hand: [],
    drawPile: shuffled.values,
    discardPile: [],
    exhaustPile: [],
    enemies: encounter.enemies.map((definitionId) => {
      const definition = registry.enemies[definitionId];
      if (!definition) throw new GameRuleError(`Unknown enemy: ${definitionId}`);
      const difficultyHp = run.difficulty === 'deepening'
        ? Math.ceil(definition.maxHp * 1.15)
        : definition.maxHp;
      const enemyStatuses = emptyStatuses();
      if (run.difficulty === 'deepening') enemyStatuses.strength = 1;
      return {
        id: nextId(state, 'enemy'),
        definitionId,
        hp: difficultyHp,
        maxHp: difficultyHp,
        block: 0,
        statuses: enemyStatuses,
        moveIndex: 0,
        phaseIndex: 0,
      };
    }),
    gearPlayedThisCombat: 0,
  };
  state.screen = 'combat';
  run.routeOptions = [];
  events.push({ type: 'combat_started', encounterId: encounter.id });
  drawCards(state, BASE_DRAW + relicModifier(state, registry, 'openingDraw'), events);
  events.push({ type: 'turn_started', turn: 1 });
}

function dispatchPlayCard(
  state: GameState,
  command: Extract<GameCommand, { type: 'play_card' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const combat = requireCombat(state);
  if (combat.phase !== 'player') throw new GameRuleError('Cards can only be played during your turn.');
  if (!combat.hand.includes(command.cardId)) throw new GameRuleError('That card is not in your hand.');

  const instance = getCardInstance(state, command.cardId);
  const definition = resolveCardDefinition(instance, registry);
  if (definition.unplayable) throw new GameRuleError(`${definition.name} is Unplayable.`);
  if (!hasResolvableEffect(state, definition, registry)) {
    throw new GameRuleError(`${definition.name} has no effect at the current Heat.`);
  }
  if (definition.gear && (instance.durability ?? 0) <= 0) {
    throw new GameRuleError(`${definition.name} is Broken.`);
  }
  if (definition.cost > combat.energy) throw new GameRuleError('Not enough Energy.');

  const living = aliveEnemies(combat);
  if (definition.target === 'enemy') {
    if (!command.targetId || !living.some((enemy) => enemy.id === command.targetId)) {
      throw new GameRuleError('Choose a living enemy target.');
    }
  }

  combat.hand = combat.hand.filter((cardId) => cardId !== command.cardId);
  combat.energy -= definition.cost;
  events.push({
    type: 'card_played',
    cardId: instance.id,
    definitionId: instance.definitionId,
  });

  for (const effect of definition.effects) {
    applyEffect(state, effect, instance.id, command.targetId, registry, events);
  }
  updateEnemyPhases(state, registry, events);

  if (definition.gear) {
    const protectedGearPlays = relicModifier(
      state,
      registry,
      'gearProtectionPerCombat',
    );
    const relicProtects = combat.gearPlayedThisCombat < protectedGearPlays;
    combat.gearPlayedThisCombat += 1;
    if (!relicProtects && instance.durability !== undefined) {
      wearGear(state, instance, 1, registry, events);
    }
  }

  if (definition.exhaust) combat.exhaustPile.push(instance.id);
  else combat.discardPile.push(instance.id);

  if (aliveEnemies(combat).length === 0) rewardForVictory(state, registry, events);
}

function dispatchEndTurn(
  state: GameState,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const combat = requireCombat(state);
  if (combat.phase !== 'player') throw new GameRuleError('It is not your turn.');

  for (const cardId of combat.hand) {
    const instance = getCardInstance(state, cardId);
    const definition = resolveCardDefinition(instance, registry);
    if (definition.ethereal) combat.exhaustPile.push(cardId);
    else combat.discardPile.push(cardId);
  }
  combat.hand = [];
  combat.statuses.weak = Math.max(0, combat.statuses.weak - 1);
  for (const enemy of aliveEnemies(combat)) {
    enemy.statuses.vulnerable = Math.max(0, enemy.statuses.vulnerable - 1);
  }

  if (combat.heat === MAX_HEAT) {
    const overheatDamage = getOverheatDamage(state, registry);
    damagePlayer(state, overheatDamage, 'overheat', events, true);
    combat.heat = 3;
    events.push({ type: 'overheated', damage: overheatDamage });
    events.push({ type: 'heat_changed', from: MAX_HEAT, to: 3 });
  }
  if (requireRun(state).hp <= 0) {
    loseCombat(state, events);
    return;
  }

  combat.phase = 'enemy';
  const vulnerableAtEnemyPhaseStart = combat.statuses.vulnerable > 0;
  for (const enemy of aliveEnemies(combat)) {
    if (enemy.statuses.burning > 0) {
      const burning = enemy.statuses.burning;
      damageEnemy(enemy, burning, 'burning', events);
      enemy.statuses.burning = Math.max(0, burning - 1);
      updateEnemyPhases(state, registry, events);
      if (aliveEnemies(combat).length === 0) {
        rewardForVictory(state, registry, events);
        return;
      }
    }
  }

  for (const enemy of aliveEnemies(combat)) {
    executeEnemyMove(state, enemy, getEnemyIntent(state, enemy, registry), registry, events);
    if (requireRun(state).hp <= 0) {
      loseCombat(state, events);
      return;
    }
    if (aliveEnemies(combat).length === 0) {
      rewardForVictory(state, registry, events);
      return;
    }
  }

  if (vulnerableAtEnemyPhaseStart) {
    combat.statuses.vulnerable = Math.max(0, combat.statuses.vulnerable - 1);
  }
  combat.block = 0;
  combat.turn += 1;
  combat.phase = 'player';
  combat.energy = combat.maxEnergy;
  drawCards(state, BASE_DRAW + relicModifier(state, registry, 'drawPerTurn'), events);
  events.push({ type: 'turn_started', turn: combat.turn });
}

function dispatchChooseReward(
  state: GameState,
  command: Extract<GameCommand, { type: 'choose_reward' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (state.screen !== 'reward' || !state.reward) throw new GameRuleError('No reward is available.');
  const run = requireRun(state);

  if (command.cardDefinitionId) {
    if (!state.reward.cardIds.includes(command.cardDefinitionId)) {
      throw new GameRuleError('That card is not one of the offered rewards.');
    }
    const card = makeCardInstance(state, command.cardDefinitionId, registry);
    run.deck.push(card);
    events.push({ type: 'card_added', cardId: card.id, definitionId: card.definitionId });
  }

  if (state.reward.relicIds.length > 0) {
    if (!command.relicId || !state.reward.relicIds.includes(command.relicId)) {
      throw new GameRuleError('Choose one of the offered relics.');
    }
    acquireRelic(state, command.relicId, registry, events);
  } else if (command.relicId) {
    throw new GameRuleError('No relic reward is available.');
  }

  run.iron += state.reward.iron;
  if (state.reward.iron !== 0) events.push({ type: 'iron_changed', amount: state.reward.iron });
  events.push({
    type: 'reward_chosen',
    cardDefinitionId: command.cardDefinitionId,
    relicId: command.relicId,
  });
  delete state.reward;
  delete state.combat;
  completeNode(state, registry);
}

function dispatchChooseRoute(
  state: GameState,
  command: Extract<GameCommand, { type: 'choose_route' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (state.screen !== 'route') throw new GameRuleError('No route choice is available.');
  const run = requireRun(state);
  const option = run.routeOptions.find((candidate) => candidate.id === command.optionId);
  if (!option) throw new GameRuleError('Unknown route option.');
  events.push({ type: 'route_chosen', optionId: option.id, kind: option.kind });

  switch (option.kind) {
    case 'combat':
    case 'elite':
    case 'boss':
      if (!option.encounterId) throw new GameRuleError('Combat route is missing an encounter.');
      dispatchStartCombat(state, { type: 'start_combat', encounterId: option.encounterId }, registry, events);
      return;
    case 'event':
      if (!option.eventId || !registry.events[option.eventId]) {
        throw new GameRuleError('Event route is missing its event.');
      }
      state.event = { eventId: option.eventId };
      state.screen = 'event';
      return;
    case 'trader':
      generateShop(state, registry);
      state.screen = 'trader';
      return;
    case 'forge':
      state.screen = 'forge';
  }
}

function applyEventEffect(
  state: GameState,
  effect: EventEffect,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const run = requireRun(state);
  switch (effect.kind) {
    case 'hp':
      run.hp = Math.max(0, Math.min(run.maxHp, run.hp + effect.amount));
      return;
    case 'iron':
      run.iron = Math.max(0, run.iron + effect.amount);
      events.push({ type: 'iron_changed', amount: effect.amount });
      return;
    case 'max_hp':
      run.maxHp = Math.max(1, run.maxHp + effect.amount);
      run.hp = Math.min(run.maxHp, Math.max(1, run.hp));
      return;
    case 'add_card': {
      const card = makeCardInstance(state, effect.cardDefinitionId, registry);
      run.deck.push(card);
      events.push({ type: 'card_added', cardId: card.id, definitionId: card.definitionId });
      return;
    }
    case 'choose_upgrade':
    case 'choose_remove':
      return;
    case 'repair_all':
      repairAllGear(state, registry, events);
      return;
    case 'add_relic':
      acquireRelic(state, effect.relicId, registry, events);
  }
}

export function getEventChoiceUnavailableReason(
  state: GameState,
  choice: EventChoiceDefinition,
  registry: ContentRegistry = defaultContent,
): string | undefined {
  const run = state.run;
  if (!run) return 'No active expedition is available.';

  for (const effect of choice.effects) {
    if (effect.kind === 'choose_upgrade') {
      const canUpgrade = run.deck.some((card) =>
        !card.upgraded && Boolean(registry.cards[card.definitionId]?.upgrade),
      );
      if (!canUpgrade) return 'No cards can be upgraded. Choose another event option.';
    }
    if (effect.kind === 'choose_remove' && run.deck.length <= 5) {
      return 'The deck cannot be reduced below 5 cards. Choose another event option.';
    }
  }

  return undefined;
}

function dispatchChooseEvent(
  state: GameState,
  command: Extract<GameCommand, { type: 'choose_event' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (state.screen !== 'event' || !state.event) throw new GameRuleError('No event is active.');
  if (state.event.pendingAction) throw new GameRuleError('Choose a card before resolving this event.');
  const event = registry.events[state.event.eventId];
  if (!event) throw new GameRuleError(`Unknown event: ${state.event.eventId}`);
  const choice = event.choices.find((candidate) => candidate.id === command.choiceId);
  if (!choice) throw new GameRuleError('Unknown event choice.');
  const unavailableReason = getEventChoiceUnavailableReason(state, choice, registry);
  if (unavailableReason) throw new GameRuleError(unavailableReason);
  const selection = choice.effects.find(
    (effect) => effect.kind === 'choose_upgrade' || effect.kind === 'choose_remove',
  );
  for (const effect of choice.effects) {
    if (effect.kind !== 'choose_upgrade' && effect.kind !== 'choose_remove') {
      applyEventEffect(state, effect, registry, events);
    }
  }
  if (requireRun(state).hp <= 0) {
    state.screen = 'defeat';
    events.push({ type: 'combat_lost' });
    return;
  }
  if (selection) {
    state.event.pendingAction = selection.kind === 'choose_upgrade' ? 'upgrade' : 'remove';
    state.event.pendingChoiceId = choice.id;
    return;
  }
  events.push({ type: 'event_resolved', eventId: event.id, choiceId: choice.id });
  completeNode(state, registry);
}

function dispatchChooseEventCard(
  state: GameState,
  command: Extract<GameCommand, { type: 'choose_event_card' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (state.screen !== 'event' || !state.event?.pendingAction || !state.event.pendingChoiceId) {
    throw new GameRuleError('No event card choice is active.');
  }
  const run = requireRun(state);
  const cardIndex = run.deck.findIndex((card) => card.id === command.cardId);
  const card = run.deck[cardIndex];
  if (!card) throw new GameRuleError('Unknown card.');

  if (state.event.pendingAction === 'upgrade') {
    const definition = registry.cards[card.definitionId];
    if (card.upgraded || !definition?.upgrade) throw new GameRuleError('That card cannot be upgraded.');
    card.upgraded = true;
    events.push({ type: 'card_upgraded', cardId: card.id });
  } else {
    if (run.deck.length <= 5) throw new GameRuleError('The deck cannot be reduced any further.');
    run.deck.splice(cardIndex, 1);
    events.push({ type: 'card_removed', cardId: card.id });
  }

  const eventId = state.event.eventId;
  const choiceId = state.event.pendingChoiceId;
  events.push({ type: 'event_resolved', eventId, choiceId });
  completeNode(state, registry);
}

function dispatchForge(
  state: GameState,
  command: Extract<GameCommand, { type: 'forge' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  if (state.screen !== 'forge') throw new GameRuleError('No forge is available.');
  const run = requireRun(state);
  if (command.action === 'heal') {
    if (run.hp >= run.maxHp) throw new GameRuleError('Health is already full.');
    run.hp = Math.min(run.maxHp, run.hp + Math.ceil(run.maxHp * 0.25));
  } else if (command.action === 'repair') {
    const hasDamagedGear = run.deck.some((instance) => {
      const definition = resolveCardDefinition(instance, registry);
      return definition.gear &&
        instance.durability !== undefined &&
        instance.durability < definition.gear.maxDurability;
    });
    if (!hasDamagedGear) throw new GameRuleError('All Gear is already fully repaired.');
    repairAllGear(state, registry, events);
  } else {
    if (!command.cardId) throw new GameRuleError('Choose a card to upgrade.');
    const card = getCardInstance(state, command.cardId);
    const definition = registry.cards[card.definitionId];
    if (!definition?.upgrade || card.upgraded) throw new GameRuleError('That card cannot be upgraded.');
    card.upgraded = true;
    events.push({ type: 'card_upgraded', cardId: card.id });
  }
  events.push({ type: 'forge_used', action: command.action, cardId: command.cardId });
  completeNode(state, registry);
}

function dispatchLeaveForge(state: GameState, registry: ContentRegistry): void {
  if (state.screen !== 'forge') throw new GameRuleError('No forge is available.');
  requireRun(state);
  completeNode(state, registry);
}

function requireShop(state: GameState) {
  if (state.screen !== 'trader' || !state.shop) throw new GameRuleError('No trader is available.');
  return state.shop;
}

function spendIron(run: RunState, amount: number, events: GameEvent[]): void {
  if (run.iron < amount) throw new GameRuleError('Not enough Iron.');
  run.iron -= amount;
  events.push({ type: 'iron_changed', amount: -amount });
}

function dispatchShopBuyCard(
  state: GameState,
  command: Extract<GameCommand, { type: 'shop_buy_card' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const shop = requireShop(state);
  const offer = shop.cards.find((candidate) => candidate.id === command.offerId);
  if (!offer || offer.sold) throw new GameRuleError('That card is not for sale.');
  const run = requireRun(state);
  spendIron(run, offer.price, events);
  const card = makeCardInstance(state, offer.cardDefinitionId, registry);
  run.deck.push(card);
  offer.sold = true;
  events.push({ type: 'card_added', cardId: card.id, definitionId: card.definitionId });
}

function dispatchShopBuyRelic(
  state: GameState,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const shop = requireShop(state);
  const offer = shop.relic;
  if (!offer || offer.sold) throw new GameRuleError('No relic is for sale.');
  spendIron(requireRun(state), offer.price, events);
  acquireRelic(state, offer.relicId, registry, events);
  offer.sold = true;
}

function dispatchShopRepair(
  state: GameState,
  command: Extract<GameCommand, { type: 'shop_repair' }>,
  registry: ContentRegistry,
  events: GameEvent[],
): void {
  const shop = requireShop(state);
  const card = getCardInstance(state, command.cardId);
  const definition = resolveCardDefinition(card, registry);
  if (!definition.gear || card.durability === undefined) throw new GameRuleError('Choose a Gear card.');
  if (card.durability >= definition.gear.maxDurability) throw new GameRuleError('That Gear is not damaged.');
  spendIron(requireRun(state), shop.repairPrice, events);
  card.durability = definition.gear.maxDurability;
  events.push({ type: 'gear_repaired', cardId: card.id, durability: card.durability });
}

function dispatchShopRemove(
  state: GameState,
  command: Extract<GameCommand, { type: 'shop_remove' }>,
  events: GameEvent[],
): void {
  const shop = requireShop(state);
  if (shop.removalUsed) throw new GameRuleError('The trader has already removed a card.');
  const run = requireRun(state);
  if (run.deck.length <= 5) throw new GameRuleError('The deck cannot be reduced any further.');
  const cardIndex = run.deck.findIndex((card) => card.id === command.cardId);
  if (cardIndex < 0) throw new GameRuleError('Unknown card.');
  spendIron(run, shop.removalPrice, events);
  const [removed] = run.deck.splice(cardIndex, 1);
  shop.removalUsed = true;
  if (removed) events.push({ type: 'card_removed', cardId: removed.id });
}

function dispatchLeaveTrader(
  state: GameState,
  registry: ContentRegistry,
): void {
  requireShop(state);
  completeNode(state, registry);
}

export function dispatch(
  currentState: GameState,
  command: GameCommand,
  registry: ContentRegistry = defaultContent,
): EngineResult {
  const state = structuredClone(currentState);
  const events: GameEvent[] = [];

  switch (command.type) {
    case 'start_run':
      dispatchStartRun(state, command, registry, events);
      break;
    case 'choose_route':
      dispatchChooseRoute(state, command, registry, events);
      break;
    case 'start_combat':
      dispatchStartCombat(state, command, registry, events);
      break;
    case 'play_card':
      dispatchPlayCard(state, command, registry, events);
      break;
    case 'end_turn':
      dispatchEndTurn(state, registry, events);
      break;
    case 'choose_reward':
      dispatchChooseReward(state, command, registry, events);
      break;
    case 'choose_event':
      dispatchChooseEvent(state, command, registry, events);
      break;
    case 'choose_event_card':
      dispatchChooseEventCard(state, command, registry, events);
      break;
    case 'forge':
      dispatchForge(state, command, registry, events);
      break;
    case 'leave_forge':
      dispatchLeaveForge(state, registry);
      break;
    case 'shop_buy_card':
      dispatchShopBuyCard(state, command, registry, events);
      break;
    case 'shop_buy_relic':
      dispatchShopBuyRelic(state, registry, events);
      break;
    case 'shop_repair':
      dispatchShopRepair(state, command, registry, events);
      break;
    case 'shop_remove':
      dispatchShopRemove(state, command, events);
      break;
    case 'leave_trader':
      dispatchLeaveTrader(state, registry);
      break;
    case 'abandon_run':
      return { state: createInitialState(), events };
  }

  return { state, events };
}

export function isCardPlayable(
  state: GameState,
  cardId: string,
  registry: ContentRegistry = defaultContent,
): boolean {
  if (state.screen !== 'combat' || state.combat?.phase !== 'player') return false;
  const instance = state.run?.deck.find((card) => card.id === cardId);
  if (!instance) return false;
  const definition = resolveCardDefinition(instance, registry);
  if (definition.cost > state.combat.energy) return false;
  if (definition.unplayable) return false;
  if (!hasResolvableEffect(state, definition, registry)) return false;
  if (definition.gear && (instance.durability ?? 0) <= 0) return false;
  return true;
}
